import React from "react"
import styled, { css, FlattenSimpleInterpolation } from "styled-components"
import { IconArrowRight } from "styleguide/icons"
import media from "styleguide/styles/media-queries"
import { ButtonOutlinedGatsbyLink, ButtonOutlinedLink } from "./Button"

type Size = "XL" | "L" | "M" | "S"

const sizes: Size[] = ["XL", "L", "M", "S", "S", "S", "S"]

const sizeToCssMapping: { [k in Size]: FlattenSimpleInterpolation } = {
  XL: css`
    padding: calc(20px - 2px);
    padding-left: 40px;
    padding-right: 32px;
    font-size: 18px;
    line-height: 24px;
  `,
  L: css`
    padding: calc(12px - 2px);
    padding-left: 32px;
    padding-right: 24px;
    font-size: 16px;
    line-height: 24px;
  `,
  M: css`
    padding: calc(8px - 2px);
    padding-left: 24px;
    padding-right: 16px;
    font-size: 14px;
    line-height: 24px;
  `,
  S: css`
    font-size: 13px;
    line-height: 16px;
    padding: calc(8px - 2px);
    padding-left: 16px;
    padding-right: 16px;
  `,
}

const svgSizeToCssMapping: { [k in Size]: FlattenSimpleInterpolation } = {
  XL: css`
    svg {
      width: 24px;
      height: 24px;
      margin-left: 16px;
    }
  `,
  L: css`
    svg {
      width: 20px;
      height: 20px;
      margin-left: 16px;
    }
  `,
  M: css`
    svg {
      width: 16px;
      height: 16px;
      margin-left: 8px;
    }
  `,
  S: css`
    svg {
      width: 16px;
      height: 16px;
      margin-left: 8px;
    }
  `,
}

const GoToButtonStyled = styled(ButtonOutlinedLink)<{ size: Size }>`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  font-weight: 600;
  & > * {
    flex: 0 0 auto;
  }

  box-sizing: border-box;

  ${props => {
    const ind = sizes.indexOf(props.size)

    return css`
      ${sizeToCssMapping[sizes[ind]]}
      ${svgSizeToCssMapping[sizes[ind]]}

      ${media.desktop`
        ${sizeToCssMapping[sizes[ind + 1]]}
        ${svgSizeToCssMapping[sizes[ind + 1]]}
      `}

      ${media.tablet`
        ${sizeToCssMapping[sizes[ind + 2]]}
        ${svgSizeToCssMapping[sizes[ind + 2]]}
      `}

      ${media.phone`
        ${sizeToCssMapping[sizes[ind + 3]]}
        ${svgSizeToCssMapping[sizes[ind + 3]]}
      `}
    `
  }}
`

// TODO: refactor this
const GoToButtonGatsbyStyled = styled(ButtonOutlinedGatsbyLink)<{ size: Size }>`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  font-weight: 600;
  & > * {
    flex: 0 0 auto;
  }

  box-sizing: border-box;

  ${props => {
    const ind = sizes.indexOf(props.size)

    return css`
      ${sizeToCssMapping[sizes[ind]]}
      ${svgSizeToCssMapping[sizes[ind]]}

      ${media.desktop`
        ${sizeToCssMapping[sizes[ind + 1]]}
        ${svgSizeToCssMapping[sizes[ind + 1]]}
      `}

      ${media.tablet`
        ${sizeToCssMapping[sizes[ind + 2]]}
        ${svgSizeToCssMapping[sizes[ind + 2]]}
      `}

      ${media.phone`
        ${sizeToCssMapping[sizes[ind + 3]]}
        ${svgSizeToCssMapping[sizes[ind + 3]]}
      `}
    `
  }}
`
const StyledIcon = styled(({ icon, color, ...props }) =>
  React.cloneElement(icon, { ...props, color })
)``

// TODO: add type
const GoToButton = ({
  children,
  size = "M",
  icon = <IconArrowRight />,
  asExternalLink = false,
  to = "",
  isIconOnLeft = false,
  ...otherProps
}) => {
  const [hovered, setHover] = React.useState(false)

  return asExternalLink ? (
    <GoToButtonStyled
      {...otherProps}
      size={size as Size}
      onMouseLeave={() => setHover(false)}
      onMouseEnter={() => setHover(true)}
    >
      {isIconOnLeft ? (
        <>
          <StyledIcon icon={icon} color={hovered ? "red" : "darkerRed"} />
          <span>{children}</span>
        </>
      ) : (
        <>
          <span>{children}</span>
          <StyledIcon icon={icon} color={hovered ? "red" : "darkerRed"} />
        </>
      )}
    </GoToButtonStyled>
  ) : (
    <GoToButtonGatsbyStyled
      {...otherProps}
      to={to}
      size={size as Size}
      onMouseLeave={() => setHover(false)}
      onMouseEnter={() => setHover(true)}
    >
      <span>{children}</span>
      <StyledIcon icon={icon} color={hovered ? "red" : "darkerRed"} />
    </GoToButtonGatsbyStyled>
  )
}

export default GoToButton
